import { reactive } from 'vue';

import { type DocumentRevision, documentApi } from '@/entities/Document';

export type PreviousRevisionsStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface PreviousRevisionsState {
  status: PreviousRevisionsStatus;
  revisions: DocumentRevision[];
}

const IDLE_STATE: PreviousRevisionsState = { status: 'idle', revisions: [] };

/**
Per-document cache of previous revisions, owning only the fetch — expansion state stays in useDocumentSelectionModal.ts.
*/
export function usePreviousRevisionsCache() {
  const stateByDocumentId = reactive(new Map<number, PreviousRevisionsState>());

  function getState(documentId: number): PreviousRevisionsState {
    return stateByDocumentId.get(documentId) ?? IDLE_STATE;
  }

  async function load(documentId: number, excludeRevisionId: number): Promise<void> {
    stateByDocumentId.set(documentId, { status: 'loading', revisions: [] });
    try {
      const revisions = await documentApi.getDocumentPreviousRevisions(
        documentId,
        excludeRevisionId
      );
      stateByDocumentId.set(documentId, { status: 'loaded', revisions });
    } catch {
      stateByDocumentId.set(documentId, { status: 'error', revisions: [] });
    }
  }

  function ensureLoaded(documentId: number, excludeRevisionId: number): void {
    const state = getState(documentId);
    if (state.status === 'idle' || state.status === 'error') {
      void load(documentId, excludeRevisionId);
    }
  }

  function retry(documentId: number, excludeRevisionId: number): void {
    void load(documentId, excludeRevisionId);
  }

  function reset(): void {
    stateByDocumentId.clear();
  }

  return {
    getState,
    ensureLoaded,
    retry,
    reset,
  };
}
